import { Result } from '@bluevision/components';
import * as Sentry from '@sentry/browser';
import React from 'react';
import { PageLoading } from '@ant-design/pro-layout';
import { checkReloadApp } from '@/utils/versionCheck/index';

import i18n from '@/i18n';
import { withRouter } from 'umi';
import type { RouteComponentProps } from 'react-router-dom';

import styles from './styles.less';

const chunkLoadErrorReg = /Loading chunk (\d)+ failed/g;

type ErrorBoundaryState = {
  /** 是否存在错误 */
  hasError: boolean;
  /** 存在错误时的错误信息 */
  errorInfo: string;
  /** 当 chunk load error 时，是否正在刷新页面 */
  chunkErrorLoading: boolean;
};

// eslint-disable-next-line @typescript-eslint/ban-types
class ErrorBoundary extends React.Component<
  RouteComponentProps & { children?: React.ReactNode },
  ErrorBoundaryState
> {
  state = { hasError: false, errorInfo: '', chunkErrorLoading: false };

  componentDidMount() {
    // 监听路由变化,重置错误状态
    this.props.history.listen(() => {
      this.setState(() => ({
        hasError: false,
        errorInfo: '',
        chunkErrorLoading: false,
      }));
    });
  }

  static getDerivedStateFromError(error: Error) {
    const errorState = {
      hasError: true,
      errorInfo: error.message,
      chunkErrorLoading: false,
    };

    if (error?.message?.match(chunkLoadErrorReg)) {
      checkReloadApp({
        onCheckPass: () => {
          // 如果没有新版本，那么是其他原因导致，上报错误
          // 上报全局错误
          Sentry.captureException(error, {
            extra: {
              type: 'error boundary catch',
            },
          });
        },
      });
      // 处理其他错误
      return errorState;
    } else {
      // 上报全局错误
      Sentry.captureException(error, {
        extra: {
          type: 'error boundary catch',
        },
      });
      // 处理其他错误
      return errorState;
    }
  }

  handleClick = () => {
    window.location.reload();
  };

  render() {
    if (this.state.chunkErrorLoading) {
      return <PageLoading />;
    }

    if (this.state.hasError) {
      return (
        <div className={styles['error-boundary-container']}>
          <Result
            result='error'
            mainText='Something went wrong.'
            subText={this.state.errorInfo}
            actions={[
              {
                id: '1',
                children: i18n.t('refresh_page'),
                onClick: this.handleClick,
                type: 'primary',
              },
            ]}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default withRouter(ErrorBoundary);
